/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx, Interpolation } from '@emotion/react';
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getGetIdFn, noop, useForkRef } from '../utils';
import useEvents from '../events/useEvents';
import { OnChangeEvent, InputEvent } from '../events/EventProvider';
import useTheme from '../style/useTheme';
import { ThemeConfig } from '../style/theme';
import { TypographyVariant } from '../style/typography';
import InputOutline from './InputOutline';

export type Props = {
  adornmentLeft?: React.ReactNode;
  adornmentRight?: React.ReactNode;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  error?: boolean;
  label?: string;
  required?: boolean;
  ariaLabel?: string;
  readOnly?: boolean;
  eventId?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: () => void;
  value?: string;
  placeholder?: string;
  clearable?: boolean;
  pattern?: string;
  autoCapitalize?: 'off';
  autoCorrect?: 'off';
  autoFocus?: boolean;
  minLength?: number;
  maxLength?: number;
  title?: string;
  componentName?: string;
  type?: React.HTMLInputTypeAttribute;
  typography?: TypographyVariant;
  inputMode?:
    | 'text'
    | 'search'
    | 'none'
    | 'email'
    | 'tel'
    | 'url'
    | 'numeric'
    | 'decimal';
  /**
   * @property lastFocusedAt - UNIX timestamp to trigger focus (use Date.now())
   */
  lastFocusedAt?: number;
  'data-testid'?: string;
};

const BaseInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    onChange = noop,
    onFocus = noop,
    onBlur = noop,
    label,
    required,
    value,
    placeholder,
    readOnly,
    disabled,
    error,
    type,
    componentName = 'BaseInput',
    typography = 'body',
    pattern,
    autoCapitalize,
    autoCorrect,
    autoFocus = false,
    minLength,
    maxLength,
    inputMode,
    ariaLabel,
    lastFocusedAt,
    eventId,
    adornmentLeft,
    adornmentRight,
    title,
    'data-testid': dataTestID,
  } = props;
  const inputRef = useRef<HTMLInputElement>();

  const handleRef: React.Ref<HTMLInputElement> = useForkRef(ref, inputRef);

  const { theme, resolveColor, resolveTypography } = useTheme();

  // START focus/blur logic
  const [isActuallyFocused, setActuallyFocused] = useState(!!lastFocusedAt);

  useEffect(() => {
    if (!isActuallyFocused || !inputRef.current) return;
    const element = inputRef.current;

    element.focus();
  }, [inputRef, isActuallyFocused]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.addEventListener('blur', () => setActuallyFocused(false));
  }, [ref]);

  useEffect(() => {
    setActuallyFocused(!!lastFocusedAt);
  }, [lastFocusedAt]);

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
    setActuallyFocused(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = () => {
    setActuallyFocused(false);
    if (onBlur) {
      onBlur();
    }
  };

  const id = useMemo(
    () => getGetIdFn(componentName)(label),
    [componentName, label]
  );

  const triggerChangeEvent = useEvents<OnChangeEvent<InputEvent>>(
    eventId,
    'Input'
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      triggerChangeEvent({
        value: e.target.value,
      });
      onChange(e);
    },
    [triggerChangeEvent, onChange]
  );

  const inputCss: Interpolation<ThemeConfig> = {
    ...resolveTypography(typography),
    border: 'none',
    outline: 'none',
    resize: 'none',
    color: resolveColor(theme.components.input['text-color']),
    backgroundColor: 'transparent',
    flexGrow: 1,
    /*
     * https://stackoverflow.com/questions/29580704/change-input-background-to-transparent-on-autocomplete-form
     */
    '&:-webkit-autofill': {
      WebkitTransitionDelay: '9999s',
      transitionDelay: '9999s',
    },
  };

  return (
    <InputOutline error={error}>
      {adornmentLeft}
      <input
        data-testid={dataTestID || id}
        css={inputCss}
        id={id}
        name={label}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        required={required}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        type={type}
        title={title}
        pattern={pattern}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        // This is a valid attribute to use on pages whose primary purpose is a single form.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        minLength={minLength}
        maxLength={maxLength}
        inputMode={inputMode}
        aria-label={ariaLabel || id}
        ref={handleRef}
      />
      {adornmentRight}
    </InputOutline>
  );
});

export default BaseInput;
